// ============================================================
// Chess Arena — Ability Collection Screen
// Grid view of all abilities with shard progress and charges
// ============================================================

import type { Screen } from '../screenManager';
import { playerState } from '../../core/playerState';
import gameConfig from '../../core/gameConfig';

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
};

export class AbilityCollectionScreen implements Screen {
  readonly name = 'abilityCollection';
  private container!: HTMLElement;

  private onClose: (() => void) | null = null;

  setOnClose(cb: () => void): void {
    this.onClose = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('ac-screen');
  }

  show(): void {
    this.render();
  }

  hide(): void {
    this.container.innerHTML = '';
  }

  update(): void {
    // No periodic updates
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  private render(): void {
    const abilityCfg = gameConfig.isInitialized ? gameConfig.getAbilityConfig() : null;
    if (!abilityCfg) {
      this.container.innerHTML = '<div class="ac-empty">Abilities not yet available.</div>';
      return;
    }

    const abilityIds = Object.keys(abilityCfg.abilities);

    const cards = abilityIds.map(id => {
      const cfg = abilityCfg.abilities[id];
      const unlocked = playerState.isAbilityUnlocked(id);
      const charges = playerState.getCharges(id);
      const shards = playerState.getShards(id);
      const shardsNeeded = cfg.shardsToUnlock;
      const rarityColor = abilityCfg.rarityColors[cfg.rarity] ?? '#888';

      if (unlocked) {
        return `
          <div class="ac-card ac-card--unlocked" style="--rarity-color: ${rarityColor}">
            <div class="ac-card-icon">
              <img src="assets/ui/${cfg.iconKey}.svg" alt="${cfg.displayName}">
            </div>
            <div class="ac-card-name">${cfg.displayName}</div>
            <div class="ac-card-rarity" style="color: ${rarityColor}">${RARITY_LABELS[cfg.rarity] ?? cfg.rarity}</div>
            <div class="ac-card-charges">${charges} charge${charges !== 1 ? 's' : ''}</div>
            <div class="ac-card-desc">${cfg.description}</div>
          </div>
        `;
      }

      // Locked — show shard progress
      const pct = shardsNeeded ? Math.min(100, (shards / shardsNeeded) * 100) : 0;
      return `
        <div class="ac-card ac-card--locked">
          <div class="ac-card-icon ac-card-icon--locked">
            <img src="assets/ui/${cfg.iconKey}.svg" alt="${cfg.displayName}">
            <div class="ac-card-lock">&#128274;</div>
          </div>
          <div class="ac-card-name">${cfg.displayName}</div>
          <div class="ac-card-rarity" style="color: ${rarityColor}">${RARITY_LABELS[cfg.rarity] ?? cfg.rarity}</div>
          <div class="ac-card-progress">
            <div class="ca-progress">
              <div class="ca-progress-fill" style="width: ${pct}%; background: ${rarityColor};"></div>
            </div>
            <div class="ac-card-shard-count">${shards}/${shardsNeeded ?? '?'} shards</div>
          </div>
          <div class="ac-card-desc">${cfg.description}</div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div class="ca-backdrop">
        <div class="ac-panel">
          <button class="ca-close-btn" data-ref="closeBtn">&#10005;</button>
          <div class="ac-title">Abilities</div>
          <div class="ac-grid">${cards}</div>
        </div>
      </div>
    `;

    this.container.querySelector('[data-ref="closeBtn"]')
      ?.addEventListener('click', () => this.onClose?.());
  }
}
